import { cn } from "@/utils/utils";
import { useEffect, useRef, useState } from "react";
import LandingIcon from "./LandingIcon";

export interface LandingPillSelectOption {
  label: string;
  value: string;
}

interface LandingPillSelectProps {
  value: string;
  options: LandingPillSelectOption[];
  placeholder: string;
  onChange: (value: string) => void;
  icon?: "chevron" | "sort";
  className?: string;
  showPlaceholderOnTrigger?: boolean;
}

export default function LandingPillSelect({
  value,
  options,
  placeholder,
  onChange,
  icon = "chevron",
  className,
  showPlaceholderOnTrigger = false,
}: LandingPillSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const triggerLabel = showPlaceholderOnTrigger
    ? placeholder
    : selected?.label || placeholder;

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative shrink-0", className)}>
      <button
        type="button"
        className="flex h-[38px] md:h-[44px] w-full items-center justify-between gap-2 rounded-full border border-landing-input bg-white px-4 text-[14px] font-medium text-landing-text outline-none focus:border-landing-button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={!showPlaceholderOnTrigger && selected ? selected.label : undefined}
      >
        <span className="truncate">{triggerLabel}</span>
        {icon === "sort" ? (
          <LandingIcon name="sort" className="h-4 w-4 shrink-0" />
        ) : (
          <LandingIcon
            name="chevronDown"
            className={cn(
              "h-4 w-4 shrink-0 transition-transform",
              open && "rotate-180"
            )}
          />
        )}
      </button>

      {open ? (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[280px] overflow-y-auto rounded-[12px] border border-landing-input bg-white py-1 shadow-[0px_4px_12px_0px_#00000014]"
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li key={opt.value || "__all"} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  title={opt.label}
                  className={cn(
                    "flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] hover:bg-[#F5F8FC]",
                    isSelected
                      ? "font-medium text-landing-text bg-[#F0F6FC]"
                      : "text-landing-body"
                  )}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                      isSelected
                        ? "border-[#5C88C5] bg-landing-button"
                        : "border-[#D1D5DB] bg-white"
                    )}
                  >
                    {isSelected ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    ) : null}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
